'use strict'

const productRulesEngine = require('@cimpress-technology/product-rules-engine');
const QUANTITY = 'Quantity';
const OPERATOR_EQUALS = 'equals';
const KEY_VALUE_DOCUMENT = 'key-value-document';
const _clone = require('lodash.clone');
const _difference = require('lodash.difference');
const _isArray = Array.isArray;
const fill = require('fill-range');

/**
 * Product Rule Engine Client, 
 * Pass false in last param if don't need merchandised Attribute Overwride
 * @param {Array} whiteList 
 * @param {Array} rules 
 * @param {Array} dataSheets
 * @param {Array|Boolean} merchandisedAttributeValues
 * @param {Array} merchantAttributeMeta
 * @param {String} LANG
 */
const PRC = function(whiteList, rules, dataSheets = [], merchandisedAttributeValues = [], merchantAttributeMeta = [], LANG = 'en') {
    
    /**
     * Expands an array of Ranges
     * @param {Array} ranges
     * @returns {Array}
     */
    const rangeExpander = function (ranges) {
        let setOfValues = [];
        ranges.forEach(range => {
            if (!range.increment) {
                setOfValues.push(range.minimum.toString());
                setOfValues.push(range.maximum.toString());
            } else if (range.minimum === range.maximum) {
                setOfValues.push(range.minimum.toString());
            } else {
                fill(range.minimum, range.maximum, range.increment).forEach(item => {
                    setOfValues.push(item.toString());
                });
            }
        });
        return setOfValues;
    };

    /**
     * Validation function
     * @param {*} userSelection
     * @param {Boolean} postProcessRemainingOptions
     */
    const validate = function (userSelection, postProcessRemainingOptions = false) {
        const validationResponse = productRulesEngine.validate(ruleSet, typeCastUserSelection(userSelection));
        return (postProcessRemainingOptions && merchantAttributeMeta && merchantAttributeMeta.length) ? postProcessDecomposedAttributeValidation(userSelection, validationResponse, ruleSet, merchantAttributeMeta, LANG) : validationResponse;
    };

    /**
     * Generate Next Valid Combination
     * @param {Array} selectableAttributes 
     * @param {Object} userSelection 
     */
    const generateVariantWithRequestedAttributes = function (selectableAttributes, userSelection) {
        let results = [];
        const currentUserSelection = userSelection || {};
        const userSelectedAttributes = Object.keys(currentUserSelection);
        const finalSelectableAttributes = _difference(selectableAttributes, userSelectedAttributes);
        recursiveBranchGenerator(currentUserSelection, finalSelectableAttributes, results);

        // post process remainingOptions, when results have length
        if(results.length){
            let finalValidResult = results.shift();
            if(merchantAttributeMeta && merchantAttributeMeta.length){
                finalValidResult.validationResponse = postProcessDecomposedAttributeValidation(finalValidResult.userSelection, finalValidResult.validationResponse, ruleSet, merchantAttributeMeta, LANG);
            }
            return finalValidResult;
        }
        return {};
    };

    const recursiveBranchGenerator = function (userSelection, remainingKeys, results) {
        try {
            const validationResponse = validate(_clone(userSelection));
            if(validationResponse.isValid == false){
                return false;
            }
            if (remainingKeys.length === 0) {
                // Push the userSelection to final selections
                results.push({isValid: validationResponse.isValid, userSelection, validationResponse});
                return true;
            }

            const nextSelectionKey = remainingKeys.slice(0, 1);

            if (nextSelectionKey.length > 0) {
                const nextSetOfValidAttributeValues = validationResponse.remainingOptions.find(option => option.attributeKey === nextSelectionKey[0]);

                if (nextSetOfValidAttributeValues && nextSetOfValidAttributeValues.attributeKey) {
                    let masterValues = availableAttributeValues[nextSetOfValidAttributeValues.attributeKey] ? availableAttributeValues[nextSetOfValidAttributeValues.attributeKey] : [];
                    // If the next attribute is a range, expand it.
                    if (nextSetOfValidAttributeValues.type === 'listOfRanges' && nextSetOfValidAttributeValues.attributeRanges.length) {
                        // Expand all the ranges
                        let expandedValues = rangeExpander(nextSetOfValidAttributeValues.attributeRanges);
                        // Sorting remaining Option to give a better UX
                        let sortedValues = expandedValues.sort((a, b) => (a).toString().localeCompare((b).toString()));
                        sortedValues.every(attributeValue => {
                            // if attributeValue is not in masterValues then return true;
                            if(hasAttributeOverwrite && masterValues.indexOf(attributeValue) === -1){
                                // This option is not merchandised, skip this
                                return true;
                            }
                            const newUserSelection = _clone(userSelection);
                            newUserSelection[nextSelectionKey[0]] = attributeValue;
                            let bool = recursiveBranchGenerator(newUserSelection, remainingKeys.slice(1, remainingKeys.length), results);
                            if(results.length === 0){
                                // Still not got valid combo for nextSelectionKey[0]
                                return true;
                            }
                            return false;
                        });
                    } else if (nextSetOfValidAttributeValues.type === 'listOfValues' && nextSetOfValidAttributeValues.attributeValues.length) {
                        
                        // Sorting remaining Option to give a better UX
                        let sortedValues = nextSetOfValidAttributeValues.attributeValues.sort((a, b) => a.value.localeCompare(b.value));

                        sortedValues.every((attributeValue, i) => {
                            // if attributeValue is not in masterValues then return true;
                            if(hasAttributeOverwrite && masterValues.indexOf(attributeValue.value) === -1){
                                // This option is not merchandised, skip this
                                return true;
                            }
                            const newUserSelection = _clone(userSelection);
                            newUserSelection[nextSelectionKey[0]] = attributeValue.value;

                            let bool = recursiveBranchGenerator(newUserSelection, remainingKeys.slice(1, remainingKeys.length), results);
                            if(results.length === 0){
                                // Still not got valid combo for nextSelectionKey[0]
                                return true;
                            }
                            // got for nextSelectionKey[0]
                            return false;
                        });

                    }
                    else{
                        // No Valid values found
                        return false;
                    }
                }
            }
        } catch (e) {
            console.trace('PRC: This shouldn\'t be logged: ' + JSON.stringify(e.data), e);
        }
    };

    /**
     * Check if given attribute name exists in definition set
     * @param {Array} dataset 
     * @param {String} selectedAttribute 
     */
    const checkIfAttributeExists = (dataset = [], selectedAttribute) => {
        let outcome = dataset.find(definition => definition.attributeKey == selectedAttribute);
        return (outcome && outcome.attributeKey);
    };

    /**
     * Check if given value exists in rule definition
     * @param {Object} rule 
     * @param {String} value 
     */
    const checkIfMatchesRuleCombination = (rule, value) => {
        let matched = false;
        if(rule.attributeValues && rule.operator === OPERATOR_EQUALS){
            let search = rule.attributeValues.find(data => data.value === value);
            matched = (search && search.value) ? true : false;
        }
        if(rule.negate !== undefined && rule.negate === true){
            matched = !matched;
        }
        return matched;
    };

    /**
     * Evaluate valid part of User selection on an attribute change
     * @param {Object} validationResult 
     * @param {Object} completeSelection 
     * @param {String} lastSelectedAttribute 
     */
    const getValidPartOfSelection = (validationResult, completeSelection, lastSelectedAttribute) => {
        let violatedAttributes = [];
        let otherAttributes = _clone(completeSelection);
        delete otherAttributes[lastSelectedAttribute];
        let selectedAttributes = Object.keys(otherAttributes);

        if(validationResult.violatedExclusions && validationResult.violatedExclusions.length){
            validationResult.violatedExclusions.forEach(rule => {
                
                if(checkIfAttributeExists(rule.predicates, lastSelectedAttribute)){
                    // found in predicates then rule.results are violated
                    let violatedAttributeSet = rule.results.reduce((arr, definition) => {
                        if(selectedAttributes.indexOf(definition.attributeKey) !== -1 /*&& !checkIfMatchesRuleCombination(definition, otherAttributes[definition.attributeKey])*/){
                            arr.push(definition.attributeKey);
                        }
                        return arr;
                    }, []);
                    violatedAttributes = [...violatedAttributes, ...violatedAttributeSet];
                }
                else if(checkIfAttributeExists(rule.results, lastSelectedAttribute)){
                    // found in rule.results then rule.predicates are violated
                    let violatedAttributeSet = rule.predicates.reduce((arr, definition) => {
                        if(selectedAttributes.indexOf(definition.attributeKey) !== -1 /*&& !checkIfMatchesRuleCombination(definition, otherAttributes[definition.attributeKey])*/){
                            arr.push(definition.attributeKey);
                        }
                        return arr;
                    }, []);
                    violatedAttributes = [...violatedAttributes, ...violatedAttributeSet];
                }
            });
        }
        let validPartOfSelection = _clone(completeSelection);
        violatedAttributes.forEach(key => {
            delete validPartOfSelection[key];
        });

        const validationResponse = validate(_clone(validPartOfSelection));
        if(validationResponse.isValid){
            return {validPartOfSelection, violatedAttributes};
        }
        return {};
    };

    // transform product model attributes into linear array for later use
    // check if attribute has range values then expand them before storing
    const availableAttributeValues = (merchandisedAttributeValues || []).reduce((object,attribute) => {
        let values = [];
        if(
            !_isArray(attribute.attributeValues) &&
            attribute.attributeValues.typeId && 
            attribute.attributeValues.typeId === KEY_VALUE_DOCUMENT && 
            attribute.attributeValues.obj && 
            attribute.attributeValues.obj.value && 
            _isArray(attribute.attributeValues.obj.value)
            ){
            attribute.attributeValues = attribute.attributeValues.obj.value;
        }
        if(_isArray(attribute.attributeValues)){
            attribute.attributeValues.forEach(row => {
                if(row.key){
                    values.push(row.key);
                }else if(row.minimum !== undefined){
                    values = [...values,...rangeExpander([row])];
                }else{
                    console.error('PRC: ',attribute.attributeLabel, 'Invalid attribute value');
                }
            });
        }
        // @todo: might need to add sorting logic here, depending upon callers needs
        //let sortedValues = values.sort((a, b) => a.localeCompare(b))
        object[attribute.attributeLabel] = values;
        return object;
    }, {});

    const ruleSet = {
        whiteList: whiteList,
        rules: rules,
        dataSheets,
    };

    const hasAttributeOverwrite = (merchandisedAttributeValues !== false);
    
    return {
        generate: generateVariantWithRequestedAttributes,
        // Disabling external usage of this validation function
        validate: validate,
        getValidPartOfSelection,
        // common function to send response from PRC
        buildResponse: (userSelections, result, isChanged = true) => {
            return {
                isChanged,
                isValid: result.isValid,
                result,
                selection: userSelections
            }
        },
        rangeExpander
    };
};

const validateOrPredict = async (PRCInstance, userSelections, selectableAttributes, lastSelectedAttributeLabel) => {

    let result = await PRCInstance.validate(_clone(userSelections), true);
    if(result.isValid){
        // Valid, no further processing needed
        return PRCInstance.buildResponse(userSelections, result, false);
    }else{
        // Not valid
        // try getting valid part out of existing selection
        let validPartOfSelection = PRCInstance.getValidPartOfSelection(result, _clone(userSelections), lastSelectedAttributeLabel);
        let lastSelection = {
            [lastSelectedAttributeLabel]: userSelections[lastSelectedAttributeLabel]
        };

        if(validPartOfSelection.validPartOfSelection){
            // got some part as valid, try predicting remaining selectables
            let predictedResult = await PRCInstance.generate(selectableAttributes, validPartOfSelection.validPartOfSelection);
            if(predictedResult.userSelection){
                // got some value predicted
                return PRCInstance.buildResponse(predictedResult.userSelection, predictedResult.validationResponse);
            }
        }

        // Nothing is valid with the Valid part of selection
        // try predicting with last selection only.
        let fallbackPrediction = await PRCInstance.generate(selectableAttributes, lastSelection);
        if(fallbackPrediction.userSelection){
            // got some value predicted
            return PRCInstance.buildResponse(fallbackPrediction.userSelection, fallbackPrediction.validationResponse);
        }
        // Nothing is valid with given last selection
        return {
            isChanged: true,
            isValid: false,
            selection: lastSelection,
        }
    }

};

/**
 * get label from attribute object considering given LANG
 *
 * @param attributeObject
 * @param LANG
 * @returns {String|undefined}
 * @private
 */
const _getLabel = (attributeObject = {}, LANG) => {
    return (attributeObject.label && attributeObject.label[LANG]) || undefined;
};

/**
 *  check if complete set of decomposed attribute is selected
 *
 * @param selection
 * @param merchantAttributes
 * @param LANG
 * @returns {boolean|Array}
 */
const ifAllDecomposedAttributesAreSelected = (selection = {}, merchantAttributes = [], LANG = 'en') => {
    if (merchantAttributes && merchantAttributes.length) {
        // pre process merchandising attribute data coming from API
        // This have limited support for `1 -> n` split only.
        let decompositionMeta = merchantAttributes
            .filter(meta => meta.mcpAttributes && meta.mcpAttributes.length === 1)
            .map(meta => {
                return {
                    name: _getLabel(_clone(meta.mcpAttributes).shift(), LANG),
                    attributes: meta.merchandisedAttributes.map(a => _getLabel(a, LANG))
                };
            });
        // No need to do anything if decomposition don't exists
        if (!decompositionMeta.length) {
            return false;
        }
        let keys = Object.keys(selection);
        let userCompletedSelectionFor = decompositionMeta.filter(decomposition => {
            if (selection[decomposition.name] !== undefined) {
                // If user itself has selected parent attribute
                // then no need to run all this logic
                return false;
            } else if (decomposition.attributes && decomposition.attributes.length > 1 && (decomposition.attributes || []).every(name => keys.includes(name))) {
                // User has selected all of attribute from this Decomposition set.
                return true;
            }
            return false;
        });
        return userCompletedSelectionFor.length ? userCompletedSelectionFor : false;
    }
    return false;
};

const constructRemainingOptionsOverride = (selection, completedAttributeDecompositions, ruleSet) => {
    let selectionCopy = Object.assign({}, selection);
    let remainingOptionOverrides = [];
    completedAttributeDecompositions.forEach(decomposition => {
        (decomposition.attributes || []).forEach(name => {
            let partialSelection = Object.assign({}, selectionCopy);
            delete partialSelection[name];
            let result = productRulesEngine.validate(ruleSet, typeCastUserSelection(partialSelection));
            if (result && result.isValid && result.remainingOptions) {
                let overrideOption = result.remainingOptions.find(option => option.attributeKey === name);
                if (overrideOption) {
                    remainingOptionOverrides.push(overrideOption);
                }
            }
        });
    });
    return remainingOptionOverrides;
};

/**
 *  Process PRC result to rectify remaining options of decomposed attributes
 *
 * @param selection
 * @param validationResult
 * @param ruleSet
 * @param merchantAttributeMeta
 * @param LANG
 * @returns {{remainingOptions,isValid}}
 */
const postProcessDecomposedAttributeValidation = (selection, validationResult, ruleSet, merchantAttributeMeta, LANG) => {
    if (validationResult && validationResult.isValid && validationResult.remainingOptions) {
        let decomposedAttributesAreSelected = ifAllDecomposedAttributesAreSelected(selection, merchantAttributeMeta, LANG);
        if (!decomposedAttributesAreSelected) {
            // No need to post process if non of decomposition got fully selected.
            return validationResult;
        }

        let overrides = constructRemainingOptionsOverride(selection, decomposedAttributesAreSelected, ruleSet);
        overrides.map(attributeOptionOverride => {
            let index = validationResult.remainingOptions.findIndex(option => option.attributeKey === attributeOptionOverride.attributeKey);
            if (index !== -1) {
                validationResult.remainingOptions[index] = attributeOptionOverride;
            }
        });
    }
    return validationResult;
};

const typeCastUserSelection = (userSelection) => {
    return Object.keys(userSelection).reduce((s, key) => {
        if(userSelection[key] !== undefined && userSelection[key] !== null){
            s[key] = userSelection[key].toString();
        }
        return s;
    }, {});
};

module.exports = {
    PRC,
    validateOrPredict,
    postProcessDecomposedAttributeValidation,
    productRulesEngine,
    typeCastUserSelection,
};


// WEBPACK FOOTER //
// ./src/product/prc.js